import { COMPOSER_LOCK } from "./chatCopy";

// 12-hour, "5:00 PM" - the same as the mobile app, whatever the browser's own locale would
// pick (some gave "17:00", others "05:00 pm")
export const formatTime = (value) => {
  const date = new Date(value);
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours % 12 || 12}:${minutes} ${hours < 12 ? "AM" : "PM"}`;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

// Chat list: today the time; this week Yesterday / the weekday; older the date
export const formatListTime = (value) => {
  const date = new Date(value);
  const today = new Date();
  const daysAgo = Math.round((startOfDay(today) - startOfDay(date)) / DAY_MS);
  if (daysAgo <= 0) return formatTime(value);
  if (daysAgo === 1) return "Yesterday";
  if (daysAgo < 7) return date.toLocaleDateString([], { weekday: "short" });
  return date.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() !== today.getFullYear() && { year: "numeric" }),
  });
};

// Links: email addresses (any ending - an address is unmistakable), http(s)://..., www.
// addresses, and bare domains on a known ending (google.com,
// ndis.gov.au/...) - a known ending only, so file.txt, e.g. or 1.2.3 stay text. Not straight
// after a letter, "@", "." or "/" (so not the domain of an email address). Group 2 is the
// link; group 1 the character before it (no lookbehind, which older Safari can't parse); sentence punctuation after one isn't part of it
const LINK =
  /(^|[^\w@./-])([a-z0-9._%+-]+@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?![\w-])|https?:\/\/[^\s<>"]+|www\.[^\s<>"]+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|edu|gov|au|nz|uk|ie|io|co|info|biz|app|dev|me|us|ca|in|sg|ai|health|care|xyz|tv|ly|link|online|site|tech|shop|store|blog|news|cloud|email|services|solutions|support|digital|agency|media|group|live|today|club|page|website)(?::\d{2,5})?(?:[/?#][^\s<>"]*)?(?![\w-]))/gi;
const PUNCTUATION = /[.,!?;:'")\]]$/;

// Where a link goes: an email opens a new message to it, anything else the web page
const linkUrl = (link) =>
  /^https?:\/\//i.test(link) ? link : /^[^/:]+@/.test(link) ? `mailto:${link}` : `https://${link}`;

const count = (text, char) => text.split(char).length - 1;

// A ")" closing a "(" inside the link is part of it, e.g. https://en.wikipedia.org/wiki/Foo_(bar)
const trimLink = (link) => {
  let trimmed = link;
  while (PUNCTUATION.test(trimmed)) {
    if (trimmed.endsWith(")") && count(trimmed, "(") >= count(trimmed, ")")) break;
    trimmed = trimmed.slice(0, -1);
  }
  return trimmed;
};

// Phone numbers, as tel: links - real ones only, so a 6-digit code, a date, an amount or a long
// reference stays text: Australian 10-digit numbers (02/03/04/07/08, 1300, 1800), +61 with
// its 9 digits, or another +country number of 8 to 15 digits. Spaces, hyphens and brackets
// may group the digits. Group 2 is the number; group 1 the character before it
const PHONE = /(^|[^\w+])(\+?\(?\d[\d ()-]{6,20}\d)(?![\w])/g;
const isPhoneNumber = (candidate) => {
  if (count(candidate, "(") !== count(candidate, ")")) return false;
  const digits = candidate.replace(/\D/g, "");
  if (candidate.startsWith("+")) {
    return digits.startsWith("61") ? /^61[23478]\d{8}$/.test(digits) : digits.length >= 8 && digits.length <= 15;
  }
  return /^(0[23478]\d{8}|1[38]00\d{6})$/.test(digits);
};

// A stretch of plain text as text and phone numbers, in order
const splitPhones = (text) => {
  const parts = [];
  let last = 0;
  PHONE.lastIndex = 0;
  for (let match = PHONE.exec(text); match; match = PHONE.exec(text)) {
    const number = match[2];
    if (!isPhoneNumber(number)) continue;
    const start = match.index + match[1].length;
    if (start > last) parts.push({ text: text.slice(last, start) });
    parts.push({ text: number, url: `tel:${number.startsWith("+") ? "+" : ""}${number.replace(/\D/g, "")}` });
    last = start + number.length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
};

// Street addresses, as a map search: a number (a unit too - "2/33", "Unit 2, 33"), one to three
// name words and a street type, then the suburb after a comma, state and postcode if there.
// A type that's also an everyday word (drive, way, close, place, court, square) needs a
// suburb, state or postcode after it - "a 2 hour drive" or "1 very close call" isn't an
// address - and a time, distance or common word can't be the street's name.
// Group 2 is the street; group 3 its type; group 4 the suburb, state and postcode
const ADDRESS_STOP = "a|an|the|and|or|of|to|in|on|at|for|with|very|more|less|so|too|my|your|our|their|his|her|its|this|that|these|those|other|another|some|any|each|every|hour|hours|hr|hrs|min|mins|minute|minutes|day|days|week|weeks|month|months|year|years|km|kms|mile|miles|metre|metres|meter|meters|second|seconds|times|people|kids|nice|good|great|long|short|big|small|bad|easy|hard|quick|slow";
const SUBURB_STOP = "and|or|but|then|at|on|in|to|for|with|is|are|was|from|near|by|please|today|tomorrow|tonight|now|thanks|thank";
const ADDRESS = new RegExp(
  "(^|[^\\w/])(" +
    "(?:(?:unit|u|apt|apartment|suite|shop|level|lvl)\\s*\\d+[a-z]?,?\\s+|\\d+[a-z]?\\/)?\\d{1,5}[a-z]?(?:-\\d{1,5}[a-z]?)?\\s+" +
    `(?:(?!(?:${ADDRESS_STOP})\\b)[a-z][a-z'.-]*\\s+){1,3}` +
    "(street|st|road|rd|avenue|ave|crescent|cres|parade|pde|highway|hwy|terrace|tce|boulevard|blvd|esplanade|esp|circuit|cct|lane|ln|grove|gr|drive|dr|way|close|cl|place|pl|court|ct|square|sq)\\.?)(?![\\w-])" +
    `((?:,\\s*(?!(?:${SUBURB_STOP})\\b)[a-z][a-z'-]*(?:\\s+(?!(?:${SUBURB_STOP})\\b)[a-z][a-z'-]*){0,2})?` +
    "(?:,?\\s+(?:nsw|vic|qld|sa|wa|tas|nt|act)\\b)?(?:,?\\s+\\d{4}\\b)?)",
  "gi"
);
const EVERYDAY_STREET_TYPE = /^(drive|dr|way|close|cl|place|pl|court|ct|square|sq)\.?$/i;
export const MAP_SEARCH = "https://www.google.com/maps/search/?api=1&query=";

// A stretch of plain text as text and street addresses, in order
const splitAddresses = (text) => {
  const parts = [];
  let last = 0;
  ADDRESS.lastIndex = 0;
  for (let match = ADDRESS.exec(text); match; match = ADDRESS.exec(text)) {
    const tail = match[4].trim();
    if (EVERYDAY_STREET_TYPE.test(match[3]) && !tail) continue;
    const address = trimLink(match[2] + match[4]);
    const start = match.index + match[1].length;
    if (start > last) parts.push({ text: text.slice(last, start) });
    parts.push({ text: address, url: `${MAP_SEARCH}${encodeURIComponent(address)}` });
    last = start + address.length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
};

// A message as plain text, links, addresses and phone numbers, in order
const splitLinks = (text) => {
  const parts = [];
  let last = 0;
  LINK.lastIndex = 0;
  for (let match = LINK.exec(text); match; match = LINK.exec(text)) {
    const link = trimLink(match[2]);
    const start = match.index + match[1].length;
    if (start > last) parts.push({ text: text.slice(last, start) });
    parts.push({ text: link, url: linkUrl(link) });
    last = start + link.length;
    LINK.lastIndex = last;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts
    .flatMap((part) => (part.url ? [part] : splitAddresses(part.text)))
    .flatMap((part) => (part.url ? [part] : splitPhones(part.text)));
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Reads mentions straight from the final text, against who's actually in the
// chat - not from tracking which autocomplete suggestions were tapped. That
// tracking missed anyone who typed "@everyone" (or a name) by hand rather than
// picking it from the dropdown, which was silently never notifying them
export const scanMentions = (text, members) => {
  const mentionEveryone = /(?:^|[^\w])@everyone(?=[^\w]|$)/i.test(text);
  const mentionedStaffIds = members
    .filter((m) => m.name && new RegExp(`(?:^|[^\\w])@${escapeRegExp(m.name)}(?=[^\\w]|$)`, "i").test(text))
    .map((m) => m.id);
  return { mentionEveryone, mentionedStaffIds };
};

// Swaps the "@partial" the picker opened for - the one starting at `at` - for the full
// "@Name " (or "@everyone "). Anchored to that position, not searched for: "@Jo" would
// otherwise also match a later "@John". Null if the text there has since changed.
// `caret` is where typing carries on: after the tag and its space
export const replaceMentionAt = (text, at, query, insert) => {
  const partial = `@${query}`;
  if (at === null || at === undefined || text.slice(at, at + partial.length) !== partial) return null;
  const rest = text.slice(at + partial.length);
  // One space after the tag - not a second one if the text already carries on with one
  const tag = rest.startsWith(" ") ? insert.trimEnd() : insert;
  return { text: `${text.slice(0, at)}${tag}${rest}`, caret: at + tag.length + (tag === insert ? 0 : 1) };
};

// Splits on links (as splitLinks does) and, within the plain-text stretches,
// on "@everyone" and each actually-mentioned person's name - so a mention
// highlights in the bubble exactly for who the message really notified, not
// any "@word" that happens to appear
export const splitMessageParts = (text, { mentionsEveryone, mentionNames = [] } = {}) => {
  const names = [...(mentionsEveryone ? ["everyone"] : []), ...mentionNames].sort(
    (a, b) => b.length - a.length
  );
  if (!names.length) return splitLinks(text);

  const mentionPattern = new RegExp(`@(?:${names.map(escapeRegExp).join("|")})(?=[^\\w]|$)`, "g");
  return splitLinks(text).flatMap((part) => {
    if (part.url) return [part];
    const parts = [];
    let last = 0;
    mentionPattern.lastIndex = 0;
    for (let match = mentionPattern.exec(part.text); match; match = mentionPattern.exec(part.text)) {
      if (match.index > last) parts.push({ text: part.text.slice(last, match.index) });
      parts.push({ text: match[0], mention: true });
      last = match.index + match[0].length;
      mentionPattern.lastIndex = last;
    }
    if (last < part.text.length) parts.push({ text: part.text.slice(last) });
    return parts.length ? parts : [part];
  });
};

// Stays one short line however many are typing
const QUOTE_CHARS = 200;
// A quote follows its original: an edit shows the new text, a delete hides it
export const toQuote = (message) => ({
  id: message.id,
  seq: message.seq,
  sender: message.sender,
  body: message.deletedAt ? null : (message.body || "").slice(0, QUOTE_CHARS),
  attachment: message.deletedAt ? null : message.attachment,
  deletedAt: message.deletedAt,
});

// What a quote says: the text, or what was sent instead of text
export const quoteText = (reply) =>
  reply.deletedAt
    ? "This message was deleted"
    : reply.body || (reply.attachment?.mimeType.startsWith("image/") ? "Photo" : reply.attachment?.name || "");

/* ----- An older copy of a message (a slow page, a late event) than the one held: it never
   undoes a delete. Both numbered (changeSeq - the chat's change count when the message last
   changed, higher is newer): the lower one is older, an equal one is the same change. Either
   unnumbered (an older server, an older snapshot): a newer edit isn't undone ----- */
export const isOlderCopy = (incoming, current) => {
  if (!!current.deletedAt && !incoming.deletedAt) return true;
  if (typeof incoming.changeSeq === "number" && typeof current.changeSeq === "number") {
    return incoming.changeSeq < current.changeSeq;
  }
  return !!current.editedAt && (!incoming.editedAt || incoming.editedAt < current.editedAt);
};

// A deleted staff profile leaves its messages with no sender - they read "Former staff"
const FORMER_STAFF = { id: "former-staff", name: "Former staff" };
const withSender = (item) => (item && !item.sender ? { ...item, sender: FORMER_STAFF } : item);
const normalizeMessage = (message) => {
  if (!message) return message;
  const normalized = withSender(message);
  if (normalized.replyTo && !normalized.replyTo.sender) normalized.replyTo = withSender(normalized.replyTo);
  if (normalized.forwardedFrom && !normalized.forwardedFrom.sender) {
    normalized.forwardedFrom = withSender(normalized.forwardedFrom);
  }
  return normalized;
};

export const isSystemMessage = (message) => message?.kind === "system";

// Merges by id and keeps seq order - socket events and REST responses can overlap
export const mergeMessages = (current, rawIncoming) => {
  const incoming = rawIncoming.map(normalizeMessage);
  const byId = new Map(current.map((message) => [message.id, message]));
  incoming.forEach((message) => {
    const existing = byId.get(message.id);
    if (!existing || !isOlderCopy(message, existing)) byId.set(message.id, message);
  });
  const quoted = new Map(incoming.map(({ id }) => [id, toQuote(byId.get(id))]));
  return [...byId.values()]
    .map((message) =>
      message.replyTo && quoted.has(message.replyTo.id)
        ? { ...message, replyTo: quoted.get(message.replyTo.id) }
        : message
    )
    .sort((a, b) => a.seq - b.seq);
};

// Changes fetched after time away (edits, deletes, reactions, pins) apply only to messages
// already in the list - one further back stays unloaded until scrolled to, so the thread
// never gets a lone old message with a gap before it. A reply here quoting one that isn't
// loaded still gets its quote brought up to date. The same as the mobile app's
// Pulls what changed (GET .../changes) from a cursor, page by page, until caught up. The
// cursor moves on only once a page has been applied, so a failure part way loses nothing:
// the next catch-up carries on from the last page applied, and a repeated cursor gets the
// same changes back (merged by id, never doubled). The same as the mobile app's
export const pullChanges = async ({ from, fetchPage, apply, advance, closed = () => false }) => {
  let cursor = from;
  for (;;) {
    const page = await fetchPage(cursor); // eslint-disable-line no-await-in-loop
    if (closed()) return;
    apply(page);
    cursor = page.cursor;
    advance(cursor);
    if (!page.hasMore) return;
  }
};

export const changesInList = (current, changed) => {
  const have = new Set(current.map((message) => message.id));
  return changed.filter((message) => have.has(message.id));
};

export const withChangedQuotes = (current, changed) => {
  const have = new Set(current.map((message) => message.id));
  const quotes = new Map(
    changed.filter((message) => !have.has(message.id)).map((message) => [message.id, toQuote(message)])
  );
  // Nothing here quotes them: the same list, so nothing redraws
  if (!current.some((message) => message.replyTo && quotes.has(message.replyTo.id))) return current;
  return current.map((message) =>
    message.replyTo && quotes.has(message.replyTo.id) ? { ...message, replyTo: quotes.get(message.replyTo.id) } : message
  );
};

// A delete for everyone the server refused: those messages come back as they were.
// `failed` is id -> { deletedAt (the one shown while it was on its way), latest (its
// copy to bring back) }. Only one still showing that delete - a real delete that came
// in meanwhile stays. Merged back, so replies quoting them un-delete too
export const restoreFailedDeletes = (current, failed) => {
  const restore = current.filter((m) => {
    const entry = failed.get(m.id);
    return !!entry && !!m.deletedAt && m.deletedAt === entry.deletedAt;
  });
  if (!restore.length) return current;
  const ids = new Set(restore.map((m) => m.id));
  return mergeMessages(
    current.filter((m) => !ids.has(m.id)),
    restore.map((m) => failed.get(m.id).latest)
  );
};

// An update (edit, pin, reaction) to a message whose delete for everyone is on its way:
// the screen keeps showing it deleted, but this copy is what comes back if the delete
// fails. `pendingDeletes` is the map restoreFailedDeletes is later given entries from
export const keepLatestWhileDeleting = (pendingDeletes, update) => {
  const entry = pendingDeletes.get(update.id);
  if (entry && !update.deletedAt) entry.latest = update;
};

// Does this message @mention me (or everyone)? Mine never count
const mentionsMe = (message, currentStaffId) =>
  !!message &&
  message.sender?.id !== currentStaffId &&
  !isSystemMessage(message) &&
  !message.deletedAt &&
  (!!message.mentionsEveryone || !!message.mentions?.some((m) => m.id === currentStaffId));

// Applies a "chat:conversation_updated" event, so lists and badges update without
// refetching. Seqs only move forward, so late or repeated events change nothing.
// System messages ("Ann added Bob") move the chat along but are never unread.
export const applyLastMessage = (conversation, rawMessage, currentStaffId) => {
  const message = normalizeMessage(rawMessage);
  if (!message || message.seq < conversation.lastSeq) return conversation;
  const isNew = message.seq > conversation.lastSeq;
  const mine = message.sender.id === currentStaffId;
  // The server marks my own messages read as they're sent
  const lastReadSeq = mine ? Math.max(conversation.lastReadSeq, message.seq) : conversation.lastReadSeq;
  const previousUnread = conversation.unreadCount ?? 0;
  const unreadCount = mine
    ? 0
    : isNew && !isSystemMessage(message)
    ? Math.min(previousUnread + 1, Math.max(message.seq - lastReadSeq, 0))
    : previousUnread;
  const unreadMentionCount = mine
    ? 0
    : (conversation.unreadMentionCount ?? 0) + (isNew && mentionsMe(message, currentStaffId) ? 1 : 0);
  return {
    ...conversation,
    lastSeq: message.seq,
    lastReadSeq,
    unreadCount,
    unreadMentionCount,
    lastMessage: message,
    // An edit of the latest message doesn't move it up the list
    lastMessageAt: isNew ? message.createdAt : conversation.lastMessageAt,
  };
};

// Read up to the end: nothing unread. Part way: never more than what's left (system
// messages in there are never counted, so the server's own count is the ceiling)
export const applyReadSeq = (conversation, readSeq) => {
  const lastReadSeq = Math.max(conversation.lastReadSeq, readSeq);
  const caughtUp = lastReadSeq >= conversation.lastSeq;
  return {
    ...conversation,
    lastReadSeq,
    unreadCount: caughtUp ? 0 : Math.min(conversation.unreadCount ?? 0, conversation.lastSeq - lastReadSeq),
    unreadMentionCount: caughtUp
      ? 0
      : Math.min(conversation.unreadMentionCount ?? 0, conversation.lastSeq - lastReadSeq),
  };
};

// I cleared the chat up to clearedSeq: its preview, unread and counts go (a newer message
// stays the preview)
export const applyCleared = (conversation, clearedSeq) => {
  const read = applyReadSeq(conversation, clearedSeq);
  const keepsPreview = !!conversation.lastMessage && conversation.lastMessage.seq > clearedSeq;
  return {
    ...read,
    lastMessage: keepsPreview ? conversation.lastMessage : null,
    ...("pinnedCount" in conversation && { pinnedCount: 0 }),
    ...("attachmentCount" in conversation && { attachmentCount: 0 }),
  };
};

// Starred chats first, then latest activity, never-used chats last - the server's order.
// An older server sends no "starred": nothing is starred then
export const sortConversations = (conversations) =>
  [...conversations].sort(
    (a, b) =>
      Number(!!b.starred) - Number(!!a.starred) ||
      new Date(b.lastMessageAt ?? 0).getTime() - new Date(a.lastMessageAt ?? 0).getTime()
  );

// Same types and limit the server's upload middleware accepts
export const ATTACHMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.heic,.doc,.docx,.xls,.xlsx,.csv";
export const MAX_ATTACHMENT_BYTES = 10 * 1000 * 1000;

// The file picker only offers these; files dropped or pasted in skip it, so are checked here
export const isAcceptedAttachment = (file) => {
  const name = file?.name || "";
  const extension = `.${name.split(".").pop()}`.toLowerCase();
  return name.includes(".") && ATTACHMENT_ACCEPT.split(",").includes(extension);
};

// Same limit the server enforces; the counter shows once a message gets close
export const MAX_MESSAGE_LENGTH = 4000;
// Files attached to one send
export const MAX_FILES = 10;
export const SHOW_COUNTER_FROM = 3500;

// Every image a browser can draw - HEIC/HEIF (iPhone photos) it can't
export const isPreviewableImage = (mimeType) =>
  mimeType.startsWith("image/") && !/^image\/hei[cf]/i.test(mimeType);

// "Peter (C1)" -> "Peter C1", so the avatar shows "PC" instead of "P("
export const initialsSource = (name = "") =>
  name
    .split(" ")
    .map((word) => word.replace(/[^A-Za-z0-9]/g, ""))
    .filter(Boolean)
    .join(" ");

// Today / Yesterday / Mon 28 Sep
export const formatDayLabel = (value) => {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
};

// 2.1 MB / 320 KB
export const formatFileSize = (bytes) => {
  if (bytes >= 1000 * 1000) return `${(bytes / (1000 * 1000)).toFixed(1)} MB`;
  return `${Math.max(Math.round(bytes / 1000), 1)} KB`;
};

// What a file's icon shows: pdf, sheet, doc, image or file
export const fileKind = (name = "", mimeType = "") => {
  const extension = name.split(".").pop().toLowerCase();
  if (mimeType === "application/pdf" || extension === "pdf") return "pdf";
  if (["csv", "xls", "xlsx"].includes(extension)) return "sheet";
  if (["doc", "docx"].includes(extension)) return "doc";
  if (mimeType.startsWith("image/")) return "image";
  return "file";
};

// A photo preview is only ever the server's own small inline image - never a file URL
const isInlinePreview = (preview) => typeof preview === "string" && /^data:image\/(jpeg|png|webp);base64,/.test(preview);

/* What a reply's quote shows - the same rules as the mobile app (lib/chatReplyQuote.ts):
   - "deleted": deleted for everyone, said in place of what it was
   - "photo": "Photo" (or its caption), with its small preview at the right when there is one
   - "file": the file's name, after an icon for its type
   - "text": up to two lines of it
   The preview comes with the quote, or from the original when it's loaded here - the same
   conversation's own copy, so nothing is fetched for it and nothing crosses chats */
export const quoteSummary = (reply, original, { photos = 1 } = {}) => {
  if (reply.deletedAt || (original?.id === reply.id && original.deletedAt)) {
    return { kind: "deleted", text: "This message was deleted", label: "This message was deleted" };
  }
  const { attachment } = reply;
  if (attachment?.mimeType?.startsWith("image/")) {
    const ownPreview = original?.id === reply.id ? original.attachment?.preview : undefined;
    const preview = [attachment.preview, ownPreview].find(isInlinePreview) || null;
    // Several sent together: how many, unless one carries a caption
    const count = photos > 1 ? `${photos} photos` : "Photo";
    const text = reply.body || count;
    return { kind: "photo", text, label: reply.body ? `Photo: ${reply.body}` : count, thumbnail: preview };
  }
  if (attachment) {
    const kind = fileKind(attachment.name, attachment.mimeType);
    return { kind: "file", fileKind: kind, text: attachment.name, label: `File: ${attachment.name}` };
  }
  return { kind: "text", text: reply.body || "", label: reply.body || "" };
};

// A reaction applied on screen before the server has it - the server's own rule: one per
// person (a new emoji replaces mine), and the same emoji again takes it off
export const applyReaction = (reactions = [], emoji, staffId, staffName) => {
  const hadIt = reactions.some((r) => r.emoji === emoji && r.staffIds.includes(staffId));
  const withoutMine = reactions
    .map((r) => {
      const index = r.staffIds.indexOf(staffId);
      if (index === -1) return r;
      return {
        ...r,
        staffIds: r.staffIds.filter((_, i) => i !== index),
        staffNames: (r.staffNames || []).filter((_, i) => i !== index),
      };
    })
    .filter((r) => r.staffIds.length);
  if (hadIt) return withoutMine;
  const existing = withoutMine.find((r) => r.emoji === emoji);
  if (!existing) return [...withoutMine, { emoji, staffIds: [staffId], staffNames: [staffName] }];
  return withoutMine.map((r) =>
    r === existing ? { ...r, staffIds: [...r.staffIds, staffId], staffNames: [...(r.staffNames || []), staffName] } : r
  );
};

// Photos (or files) sent together are one bubble, but each is its own message with its own
// reactions - a reaction is on the one photo it was given to. The bubble shows them all
// together: an emoji's count is every reaction with it, on any of them (as on mobile)
export const mergeReactions = (messages) => {
  const byEmoji = new Map();
  messages.forEach((message) =>
    (message.reactions || []).forEach((reaction) => {
      if (!reaction.staffIds.length) return;
      const into = byEmoji.get(reaction.emoji);
      byEmoji.set(
        reaction.emoji,
        into
          ? {
              emoji: reaction.emoji,
              staffIds: [...into.staffIds, ...reaction.staffIds],
              staffNames: [...(into.staffNames || []), ...(reaction.staffNames || [])],
            }
          : { ...reaction }
      );
    })
  );
  return [...byEmoji.values()];
};

// The quick reactions on offer in one tap; any other single emoji can be picked from the
// "+" (the server accepts any one emoji)
export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// Why the message box is replaced by a note. The server says why (postingBlockedReason);
// older responses without it fall back to what the chat itself says
// The same words as the mobile app (chatCopy)
const POSTING_BLOCKED_TEXT = {
  admins_only: COMPOSER_LOCK.admins_only,
  archived: COMPOSER_LOCK.archived,
  read_only_oversight: COMPOSER_LOCK.read_only_oversight,
  recipient_archived: COMPOSER_LOCK.recipient_archived,
};

export const postingBlockedText = (conversation) => {
  if (!conversation || conversation.canPost !== false) return null;
  if (POSTING_BLOCKED_TEXT[conversation.postingBlockedReason]) {
    return POSTING_BLOCKED_TEXT[conversation.postingBlockedReason];
  }
  if (conversation.archivedAt) return POSTING_BLOCKED_TEXT.archived;
  if (conversation.viewingAsAdmin) return POSTING_BLOCKED_TEXT.read_only_oversight;
  if (conversation.postingPolicy === "admins_only") return POSTING_BLOCKED_TEXT.admins_only;
  return COMPOSER_LOCK.fallback;
};

// "online", or when they were last here: "last seen today at 10:42", "last seen yesterday at 18:05"
export const formatPresence = (online, lastSeenAt) => {
  // Lowercase, as WhatsApp and the mobile app write it
  if (online) return "online";
  if (!lastSeenAt) return null;
  const date = new Date(lastSeenAt);
  const day = formatDayLabel(lastSeenAt);
  if (day === "Today") return `last seen today at ${formatTime(lastSeenAt)}`;
  if (day === "Yesterday") return `last seen yesterday at ${formatTime(lastSeenAt)}`;
  return `last seen ${date.toLocaleDateString([], { day: "numeric", month: "short" })}`;
};

// "Ann", "Ann and Bob", "Ann, Bob and 2 others" - me as "you"
const joinNames = (people, currentStaffId) => {
  const names = people.map((p) => (p?.id === currentStaffId ? "you" : p?.name || FORMER_STAFF.name));
  if (names.length <= 1) return names[0] || "someone";
  if (names.length <= 3) return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} others`;
};

// A system message's sentence, e.g. "Ann added Bob and you" / "You renamed the group to "Rota""
export const systemEventText = (event, currentStaffId) => {
  if (!event) return "";
  const actor = event.actor?.id === currentStaffId ? "You" : event.actor?.name || FORMER_STAFF.name;
  const targets = joinNames(event.targets || [], currentStaffId);
  switch (event.type) {
    case "group_created":
      return `${actor} created the group${event.name ? ` "${event.name}"` : ""}`;
    case "member_added":
      return `${actor} added ${targets}`;
    case "member_removed":
      return `${actor} removed ${targets}`;
    case "member_left":
      return `${actor} left`;
    case "renamed":
      return `${actor} renamed the group to "${event.name || ""}"`;
    case "description_changed":
      return `${actor} changed the group description`;
    case "posting_policy_changed":
      return event.postingPolicy === "admins_only"
        ? `${actor} changed the group so only admins can send messages`
        : `${actor} changed the group so everyone can send messages`;
    case "admin_promoted":
      return `${actor} made ${targets} ${(event.targets || []).length > 1 ? "admins" : "an admin"}`;
    case "admin_demoted":
      return `${actor} removed ${targets} as ${(event.targets || []).length > 1 ? "admins" : "admin"}`;
    case "archived":
      return `${actor} archived the group`;
    case "unarchived":
      return `${actor} unarchived the group`;
    default:
      return `${actor} updated the group`;
  }
};

// "Team Chat" / "Group" / "Direct message"
export const CONVERSATION_TYPE_LABEL = { team: "Team Chat", custom: "Group", direct: "Direct message" };

// "12 members"
export const memberCountLabel = (count) => `${count} ${count === 1 ? "member" : "members"}`;

// "All can post" / "Only admins can post"
export const postingPolicyLabel = (policy) => (policy === "admins_only" ? "Only admins can post" : "All can post");

// "Sarah, James, Emily, +1" - first names, me as "You"
export const reactorsLabel = (reaction, currentStaffId, shown = 3) => {
  const ids = reaction.staffIds || [];
  // Each person once - photos sent together count every reaction on them (mergeReactions),
  // but someone who reacted to three of them is still one name
  const names = ids
    .map((id, index) => [id, index])
    .filter(([id], position) => ids.indexOf(id) === position)
    .map(([id, index]) =>
      id === currentStaffId ? "You" : ((reaction.staffNames || [])[index] || "Someone").split(" ")[0]
    );
  const extra = names.length - shown;
  return extra > 0 ? `${names.slice(0, shown).join(", ")}, +${extra}` : names.join(", ");
};

// The one pill under a bubble, as in WhatsApp: its most used emojis (at most three, the
// earliest first among equals) and how many reactions there are in all
export const reactionSummary = (reactions) => ({
  emojis: reactions
    .map((reaction, index) => ({ emoji: reaction.emoji, count: (reaction.staffIds || []).length, index }))
    .sort((a, b) => b.count - a.count || a.index - b.index)
    .slice(0, 3)
    .map((reaction) => reaction.emoji),
  total: reactions.reduce((sum, reaction) => sum + (reaction.staffIds || []).length, 0),
});

// 15 Jan 2024
export const formatShortDate = (value) =>
  new Date(value).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });

// "just now", "5 minutes ago", "2 days ago" - for "Pinned by Sarah · 2 days ago"
export const formatRelative = (value) => {
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} ${days === 1 ? "day" : "days"} ago`;
  return formatShortDate(value);
};

/* ----- A chat that isn't in my list (an empty direct chat, one opened by link) keeps its
   details beside the list while it's open. Details that arrive for another chat - a late
   answer for the one open before - leave them as they are ----- */
export const openedExtraAfter = (current, detail, activeConversationId) =>
  detail.id === activeConversationId ? detail : current;
